import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { QuillModule } from 'ngx-quill';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { Version, Role, Language } from '../../models/version.model';
import { VersionService } from '../../services/version.service';
import { MarkdownService } from '../../services/markdown.service';
import { ConversionResult } from '../../models/markdown-conversion.model';

@Component({
  selector: 'changelog-editor-version-form',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, QuillModule, ConfirmDialog, ButtonModule],
  templateUrl: './version-form.component.html',
  styleUrl: './version-form.component.css',
  providers: [ConfirmationService]
})
export class VersionFormComponent implements OnInit {
  versionForm!: FormGroup;
  isEditMode = false;
  versionId: string | null = null;
  loading = false;
  saving = false;
  currentVersion?: Version;

  availableRoles: Role[] = ['ACP_USER', 'ACP_CUSTOMER'];
  availableLanguages: Language[] = ['en-US', 'de-DE'];

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean'],
      [{ 'direction': 'ltr' }]
    ]
  };

  xmlInput = '';
  markdownInput = '';
  isConvertingMarkdown = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private versionService: VersionService,
    private markdownService: MarkdownService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.versionId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = this.versionId !== null && this.versionId !== 'new';
    if (this.isEditMode && this.versionId) {
      this.loadVersion(this.versionId);
    }
  }

  initForm(): void {
    this.versionForm = this.fb.group({
      major: [0, [Validators.required, Validators.min(0)]],
      minor: [0, [Validators.required, Validators.min(0)]],
      patch: [0, [Validators.required, Validators.min(0)]],
      title: [''],
      role: [[], Validators.required],
      description: ['', Validators.required],
      language: ['en-US', Validators.required],
      releaseDate: [new Date().toISOString().split('T')[0], Validators.required]
    });
  }

  loadVersion(id: string): void {
    this.loading = true;
    this.versionService.getVersionById(id).subscribe({
      next: (version) => {
        if (version) {
          this.currentVersion = version;
          this.versionForm.patchValue({
            major: version.major,
            minor: version.minor,
            patch: version.patch,
            title: version.title || '',
            role: version.role,
            description: version.description,
            language: version.language,
            releaseDate: new Date(version.releaseDate).toISOString().split('T')[0]
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading version:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error loading version' });
        this.loading = false;
        this.router.navigate(['../..'], { relativeTo: this.route });
      }
    });
  }

  onRoleChange(role: Role, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const roles = this.versionForm.get('role')?.value || [];
    if (checked) {
      if (!roles.includes(role)) {
        this.versionForm.patchValue({ role: [...roles, role] });
      }
    } else {
      this.versionForm.patchValue({ role: roles.filter((r: Role) => r !== role) });
    }
  }

  isRoleSelected(role: Role): boolean {
    return (this.versionForm.get('role')?.value || []).includes(role);
  }

  onDescriptionChange(_content: any): void {}

  onHtmlChange(event: Event): void {
    const htmlContent = (event.target as HTMLTextAreaElement).value;
    this.versionForm.patchValue({ description: this.processDescriptionHtml(htmlContent) }, { emitEvent: false });
  }

  private processDescriptionHtml(html: string): string {
    return html
      .replace(/<ul>/g, '<ul class="space-y-2">')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+class=/g, ' class=')
      .replace(/\s{2,}/g, ' ');
  }

  convertXmlToHtml(): void {
    if (!this.xmlInput.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter XML content' });
      return;
    }
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.xmlInput, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) throw new Error('Invalid XML format');
      this.versionForm.patchValue({ description: this.xmlToHtml(xmlDoc.documentElement) });
      this.xmlInput = '';
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'XML converted to HTML successfully!' });
    } catch (error) {
      console.error('XML conversion error:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error converting XML. Please check your XML format.' });
    }
  }

  private xmlToHtml(element: Element): string {
    let html = '';
    const tagName = element.tagName.toLowerCase();
    if (tagName === 'ul' || tagName === 'list') {
      html += '<ul class="space-y-2">';
      for (const child of Array.from(element.children)) html += this.xmlToHtml(child);
      html += '</ul>';
    } else if (tagName === 'li' || tagName === 'item') {
      html += '<li>' + (element.textContent || '');
      for (const child of Array.from(element.children)) html += this.xmlToHtml(child);
      html += '</li>';
    } else if (tagName === 'p' || tagName === 'paragraph') {
      html += '<p>' + (element.textContent || '') + '</p>';
    } else if (tagName === 'h1' || tagName === 'heading1') {
      html += '<h1>' + (element.textContent || '') + '</h1>';
    } else if (tagName === 'h2' || tagName === 'heading2') {
      html += '<h2>' + (element.textContent || '') + '</h2>';
    } else {
      for (const child of Array.from(element.children)) html += this.xmlToHtml(child);
      if (element.children.length === 0 && element.textContent) html += element.textContent;
    }
    return html;
  }

  convertMarkdownToHtml(): void {
    if (!this.markdownInput.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter Markdown content' });
      return;
    }
    const currentDescription = this.versionForm.get('description')?.value;
    if (currentDescription?.trim()) {
      this.confirmationService.confirm({
        header: 'Overwrite Description?',
        message: 'This will replace your current description. Continue?',
        accept: () => this.performMarkdownConversion()
      });
    } else {
      this.performMarkdownConversion();
    }
  }

  private performMarkdownConversion(): void {
    this.isConvertingMarkdown = true;
    this.markdownService.convertToHtml(this.markdownInput).subscribe({
      next: (result: ConversionResult) => {
        if (result.success) {
          this.versionForm.patchValue({ description: result.html });
          this.markdownInput = '';
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Markdown converted successfully!' });
        } else {
          const errorMsg = result.error?.message || 'Conversion failed';
          this.messageService.add({ severity: 'error', summary: 'Error', detail: `Conversion failed: ${errorMsg}` });
        }
        this.isConvertingMarkdown = false;
      },
      error: (err) => {
        console.error('Markdown conversion error:', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unexpected error during conversion' });
        this.isConvertingMarkdown = false;
      }
    });
  }

  onSubmit(): void {
    if (this.versionForm.invalid) {
      Object.keys(this.versionForm.controls).forEach(key => this.versionForm.get(key)?.markAsTouched());
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Please fill in all required fields' });
      return;
    }
    this.saving = true;
    const formValue = this.versionForm.value;
    const versionData = {
      ...formValue,
      description: this.processDescriptionHtml(formValue.description),
      releaseDate: new Date(formValue.releaseDate)
    };
    const operation = this.isEditMode && this.versionId
      ? this.versionService.updateVersion(this.versionId, versionData)
      : this.versionService.addVersion(versionData);

    operation.subscribe({
      next: () => {
        this.saving = false;
        this.router.navigate(['../..'], { relativeTo: this.route });
      },
      error: (error) => {
        console.error('Error saving version:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error saving version. Please try again.' });
        this.saving = false;
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['../..'], { relativeTo: this.route });
  }

  getVersionNumber(): string {
    const form = this.versionForm.value;
    const patch = form.patch !== null && form.patch !== undefined && form.patch !== '' ? '.' + form.patch : '';
    return `${form.major ?? 0}.${form.minor ?? 0}${patch}`;
  }

  getReleaseDateFormatted(): string {
    const raw = this.versionForm.get('releaseDate')?.value;
    if (!raw) return '';
    return new DatePipe('en-US').transform(raw, 'dd MMM, yyyy') ?? '';
  }
}
